/**
 * Symbol-icon rules — the default, built-in manifest.
 *
 * Everything here is plain data (JSON-serialisable) so the CRM can later publish a newer
 * manifest and the terminal can swap it in; this copy is the offline / CRM-down fallback.
 *
 * Gateways know nothing about icons. A symbol is classified from the fields the terminal
 * already receives for it: its name, its group path and its description.
 */

import { STOCK_BRANDS, STOCK_TICKERS } from "./brands.generated";
import { STOCK_MARKETS } from "./brand-name";

export type SymbolIconClass =
  | "fx"
  | "crypto"
  | "metal"
  | "energy"
  | "agri"
  | "index"
  | "stock"
  | "other";

/**
 * Key templates (`$1`, `$2` are the pattern's capture groups):
 *   asset:CODE   an instrument code — manifest.assets, then a currency flag, then a coin
 *   crypto:CODE  a coin only
 *   flag:cc / cmd:name  a literal library key
 *   mono:TEXT    a monogram (never fails)
 * A rule only applies when its base resolves to an icon that exists in the library;
 * otherwise the resolver moves on. A loose pattern can therefore miss, never mislabel.
 */
export interface SymbolIconRule {
  id: string;
  cls: SymbolIconClass;
  /** Regex on the symbol name as the broker spells it. */
  pattern: string;
  flags?: string;
  /** Optional extra conditions on the symbol's other fields (case-insensitive regex). */
  where?: { group?: string; description?: string };
  base: string;
  quote?: string;
  /** When true the rule fails unless the quote resolves too (FX needs both flags). */
  quoteRequired?: boolean;
  /** A last-resort rule: tried only after the suffix-stripped alias lookup. */
  fallback?: boolean;
  /** Shares only: the ticker and exchange-suffix captures, for the logo lookup. */
  ticker?: string;
  exchange?: string;
}

export interface SymbolIconAlias {
  cls: SymbolIconClass;
  base: string;
  quote?: string;
}

export interface SymbolIconManifest {
  version: number;
  /** Currency code → flag country code. */
  currencies: Record<string, string>;
  /** Instrument code → library key, for codes that are neither a currency nor a coin ticker. */
  assets: Record<string, string>;
  /**
   * Whole-name aliases on the upper-cased name. Tried as spelled (minus a `#` prefix and a futures
   * month) before the rules, and with a broker suffix removed after them — so `GOLD.pro` is gold
   * but `GOLD.N`, which the share rule claims first, is not.
   */
  aliases: Record<string, SymbolIconAlias>;
  rules: SymbolIconRule[];
  /**
   * Share logos. A share is recognised by the rules (class "stock"); its logo is then looked up
   * by market + ticker, then by its company name as the broker describes it.
   */
  stocks: {
    /** Exchange suffix → market (`O` → `US`). */
    markets: Record<string, string>;
    /** `US:AAPL` → logo slug. */
    tickers: Record<string, string>;
    /** Normalised company name → logo slug. */
    names: Record<string, string>;
  };
}

/** Broker decoration after the instrument: `.sr`, `.pro`, `+`, `m`, `_i` … */
const SUFFIX = String.raw`(?:[._\-+!^][A-Za-z0-9]{0,6}|[a-z]{1,4})?`;

/** The broker's TOP folder says crypto — `Stocks\…\Cryptocurrency Miners\MARA.O` does not count. */
const CRYPTO_GROUP = String.raw`^[^\\/]*crypto`;

const coin = (code: string): SymbolIconAlias => ({ cls: "crypto", base: `crypto:${code}`, quote: "flag:us" });
const cmd = (cls: SymbolIconClass, name: string): SymbolIconAlias => ({ cls, base: `cmd:${name}` });
const idx = (cc: string): SymbolIconAlias => ({ cls: "index", base: `flag:${cc}` });

export const DEFAULT_SYMBOL_ICON_MANIFEST: SymbolIconManifest = {
  version: 1,

  currencies: {
    USD: "us", EUR: "eu", GBP: "gb", GBX: "gb", JPY: "jp", CHF: "ch", CAD: "ca", AUD: "au", NZD: "nz",
    SEK: "se", NOK: "no", DKK: "dk", PLN: "pl", HUF: "hu", CZK: "cz", TRY: "tr", ZAR: "za", MXN: "mx",
    SGD: "sg", HKD: "hk", CNH: "cn", CNY: "cn", RUB: "ru", ILS: "il", THB: "th", RON: "ro", BRL: "br",
    CLP: "cl", INR: "in", KRW: "kr", IDR: "id", MYR: "my", NGN: "ng", VND: "vn", PHP: "ph", TWD: "tw",
    AED: "ae", SAR: "sa", COP: "co", ARS: "ar", EGP: "eg", KES: "ke", BGN: "bg", ISK: "is", UAH: "ua",
    KZT: "kz", PKR: "pk", PEN: "pe", QAR: "qa", KWD: "kw", BHD: "bh",
  },

  assets: {
    XAU: "cmd:gold", XAG: "cmd:silver", XPT: "cmd:platinum", XPD: "cmd:palladium",
    XTI: "cmd:oil", XBR: "cmd:oil", XNG: "cmd:gas",
    XBT: "crypto:BTC", MIOTA: "crypto:IOTA", RENDER: "crypto:RNDR",
  },

  aliases: {
    // Coins spelled as words.
    BITCOIN: coin("BTC"), ETHEREUM: coin("ETH"), LITECOIN: coin("LTC"), BITCOINCASH: coin("BCH"),
    CARDANO: coin("ADA"), DOGECOIN: coin("DOGE"), POLKADOT: coin("DOT"), CHAINLINK: coin("LINK"),
    AVALANCHE: coin("AVAX"), SOLANA: coin("SOL"), STELLAR: coin("XLM"), UNISWAP: coin("UNI"),
    COSMOS: coin("ATOM"), TRON: coin("TRX"), TEZOS: coin("XTZ"), ZCASH: coin("ZEC"),
    ETHCLASSIC: coin("ETC"), FILECOIN: coin("FIL"), ALGORAND: coin("ALGO"), BINANCECOIN: coin("BNB"),
    VECHAIN: coin("VET"), SUSHISWAP: coin("SUSHI"), RIPPLE: coin("XRP"), MONERO: coin("XMR"),
    APTOS: coin("APT"), HEDERA: coin("HBAR"), IMMUTABLE: coin("IMX"), TONCOIN: coin("TON"), POLYGON: coin("POL"),

    // Metals.
    GOLD: cmd("metal", "gold"), GOLDOZ: cmd("metal", "gold"), GOLDGR: cmd("metal", "gold"),
    GOLDEURO: { cls: "metal", base: "cmd:gold", quote: "flag:eu" },
    SILVER: cmd("metal", "silver"),
    SILVEREURO: { cls: "metal", base: "cmd:silver", quote: "flag:eu" },
    PLATINUM: cmd("metal", "platinum"), PALLADIUM: cmd("metal", "palladium"),
    COPPER: cmd("metal", "copper"), ALUMINIUM: cmd("metal", "metal"), ALUMINUM: cmd("metal", "metal"),
    ZINC: cmd("metal", "metal"), LEAD: cmd("metal", "metal"), NICKEL: cmd("metal", "metal"),

    // Energy.
    WTI: cmd("energy", "oil"), BRENT: cmd("energy", "oil"), USOIL: cmd("energy", "oil"),
    UKOIL: cmd("energy", "oil"), CRUDE: cmd("energy", "oil"),
    "NAT.GAS": cmd("energy", "gas"), NATGAS: cmd("energy", "gas"), NGAS: cmd("energy", "gas"),

    // Agricultural.
    COCOA: cmd("agri", "agri"), COFFEE: cmd("agri", "agri"), CORN: cmd("agri", "agri"),
    COTTON: cmd("agri", "agri"), SBEAN: cmd("agri", "agri"), SOYBEAN: cmd("agri", "agri"),
    SUGAR: cmd("agri", "agri"), WHEAT: cmd("agri", "agri"),

    // Indices — shown by the flag of their market.
    US30: idx("us"), DJ30: idx("us"), US500: idx("us"), SPX500: idx("us"), USSPX500: idx("us"),
    US100: idx("us"), NAS100: idx("us"), USNDAQ100: idx("us"), USTEC: idx("us"), US2000: idx("us"),
    "US$IDX": idx("us"), USDX: idx("us"), DXY: idx("us"),
    GER40: idx("de"), GERMANY40: idx("de"), GERMANY50: idx("de"), GERTECH30: idx("de"), DE40: idx("de"),
    UK100: idx("gb"), FRA40: idx("fr"), FRANCE40: idx("fr"), FRANCE120: idx("fr"),
    EUR50: idx("eu"), EURO50: idx("eu"), EU50: idx("eu"), SPAIN35: idx("es"), ESP35: idx("es"),
    HOLLAND25: idx("nl"), SWI20: idx("ch"), SWISS20: idx("ch"), JP225: idx("jp"), JAPAN225: idx("jp"),
    JPN225: idx("jp"), AUS200: idx("au"), HONGKONG50: idx("hk"), HK50: idx("hk"),
    CHINAA50: idx("cn"), CHINAHSHAR: idx("cn"), ITALY40: idx("it"),
  },

  rules: [
    // EURUSD, EUR/USD, EURUSD.sr, EURUSDm — also XAUUSD and BTCUSD (any two known 3-letter codes).
    {
      id: "pair-3x3",
      cls: "fx",
      pattern: String.raw`^[#.]?([A-Z]{3})[/._\-]?([A-Z]{3})${SUFFIX}$`,
      base: "asset:$1",
      quote: "asset:$2",
      quoteRequired: true,
    },
    // BTCUSDT, BTC-USD, 1000PEPEUSDT, ETHUSDT.P, SOLUSDT-PERP.
    {
      id: "crypto-pair",
      cls: "crypto",
      pattern:
        String.raw`^[#.]?(?:10{3,6})?([A-Z0-9]{2,10}?)[/._\-]?` +
        String.raw`(USDT|USDC|BUSD|FDUSD|TUSD|DAI|USD|EUR|GBP|JPY|TRY|BRL|AUD|BTC|ETH|BNB)` +
        String.raw`(?:[._\-]?(?:P|PERP|SWAP))?${SUFFIX}$`,
      base: "crypto:$1",
      quote: "asset:$2",
    },
    // Coins spelled as an everyday word — only under a crypto folder, where the word cannot mean anything else.
    { id: "crypto-word-graph", cls: "crypto", pattern: String.raw`^GRAPH${SUFFIX}$`, flags: "i", where: { group: CRYPTO_GROUP }, base: "crypto:GRT", quote: "flag:us" },
    { id: "crypto-word-internet", cls: "crypto", pattern: String.raw`^INTERNET${SUFFIX}$`, flags: "i", where: { group: CRYPTO_GROUP }, base: "crypto:ICP", quote: "flag:us" },
    // A bare ticker (BAT, DASH, XRP) is a coin only when the broker files it under crypto.
    {
      id: "crypto-bare",
      cls: "crypto",
      pattern: String.raw`^[#.]?([A-Z0-9]{2,10})${SUFFIX}$`,
      where: { group: CRYPTO_GROUP },
      base: "crypto:$1",
    },
    // Filed under crypto but no icon for it yet: still a coin, drawn as a monogram.
    {
      id: "crypto-by-group",
      fallback: true,
      cls: "crypto",
      pattern: String.raw`^[#.]?([A-Za-z0-9]{1,10})`,
      where: { group: CRYPTO_GROUP },
      base: "mono:$1",
    },
    // Shares: ticker + exchange suffix (AAPL.O, BRKb.N, SIEGn.DE, ASML.AS). Monogram until logos land.
    {
      id: "stock-suffix",
      cls: "stock",
      pattern: String.raw`^([A-Z0-9]{1,6})[A-Za-z_]{0,4}\.(O|OQ|N|A|US|L|PA|DE|F|S|AS|BR|LS|MI|MC|ST|OL|CO|HE|VI|HK|T|AX|TO)$`,
      base: "mono:$1",
      ticker: "$1",
      exchange: "$2",
    },
    {
      id: "stock-by-group",
      fallback: true,
      cls: "stock",
      pattern: String.raw`^[#.]?([A-Za-z0-9]{1,6})`,
      where: { group: String.raw`^(stocks?|shares?|equit|etfs?)\b` },
      base: "mono:$1",
    },
  ],

  stocks: { markets: STOCK_MARKETS, tickers: STOCK_TICKERS, names: STOCK_BRANDS },
};
